
import { Component, Prop, Vue } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { extend, ValidationObserver, ValidationProvider } from "vee-validate";
import Multiselect from "vue-multiselect";
import { Getter } from "vuex-class";
import {
  IContainer,
  IDeclaredValue,
  IDeclaredValueFingerprint,
  ILimitExceeded,
  ISolCustomerCommodity,
  ISolCustomerCurrency,
  IUserClientSubclientServicepointData,
  IUserDetailData,
  IValue,
  KeyValuePair,
  SolCustomerOrderType,
  IAmountByCurrency,
} from "@/entities";
import DeclaredValueDetails from "@/components/orders/agile_pickup/DeclaredValueDetails.vue";
import { ISaveDeclaredValueRequestBody } from "@/services";
import DeclaredContainerDetails from "@/components/orders/agile_pickup/DeclaredContainerDetails.vue";

@Component({
  name: "add-edit-declared-value-modal",
  components: {
    DeclaredContainerDetails,
    DeclaredValueDetails,
    Multiselect,
    PModal,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class AddEditDeclaredValueModal extends Vue {
  @Prop({ required: true, default: "", type: String }) serviceCode!: string;
  @Prop({ required: false, default: "0", type: String }) mode!: string;
  @Prop({ required: true }) commodities!: ISolCustomerCommodity[];
  @Prop({ required: false, default: null })
  declaredValue!: IDeclaredValue;
  @Prop({ required: false, default: null })
  servicepointSelected!: IUserClientSubclientServicepointData | null;
  @Prop({ required: false, default: null }) allDeclaredValues!: IDeclaredValueFingerprint | null;
  @Prop({ required: false, default: () => [] })
  requestedAmountByCurrency!: IAmountByCurrency[];

  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getSolCustomerCurrencies") currencies!: ISolCustomerCurrency[];

  public declaredValueTypes: KeyValuePair[] = [
    { key: "BAG", value: this.$t("label.bag") },
    { key: "SEAL", value: this.$t("label.seal") },
  ];

  public type: KeyValuePair | null = this.declaredValueTypes[0];
  public code = "";
  public commodity: ISolCustomerCommodity | null = this.commodities?.[0] ?? null;
  public currency?: ISolCustomerCurrency | null = null;
  public amount: number | null = null;
  public declaredValues: IValue[] = [];
  public declaredContainers: IContainer[] = [];

  private valueEdit: IValue | null = null;
  private containerEdit: IContainer | null = null;

  $refs!: {
    formValues: InstanceType<typeof ValidationObserver>;
    formContainers: InstanceType<typeof ValidationObserver>;
  };

  async created(): Promise<void> {
    extend("code_unique", {
      validate: async (value) => {
        if (value) {
          const list = this.mode === "0" ? this.declaredValues : this.declaredContainers;

          const item = list.find((v) => {
            if (this.type?.key === "BAG") {
              return v.bc === value;
            } else if (this.type?.key === "SEAL") {
              return v.sc === value;
            }
            return false;
          });

          return !(item && item !== this.valueEdit && item !== this.containerEdit);
        }
        return true;
      },
      message: () => {
        if (this.type?.key === "BAG") {
          return this.$t("validation.messages.bagCodeAlreadyExists") as string;
        }

        return this.$t("validation.messages.sealCodeAlreadyExists") as string;
      },
    });
  }

  get maxLengthCode(): number {
    return this.type?.key === "BAG" ? 20 : this.getLoggedUser.country!.maxCodeSeal!;
  }

  initData(): void {
    this.resetFormContainers();
    this.resetFormValues();
    this.declaredValues = [];
    this.declaredContainers = [];
    this.declaredValues.push(...(this.declaredValue?.vl?.map((v) => ({ ...v })) || []));
    this.declaredContainers.push(...(this.declaredValue?.ct?.map((c) => ({ ...c })) || []));
    this.type = this.declaredValueTypes[1];
  }

  resetFormContainers(): void {
    this.containerEdit = null;

    this.code = "";
    this.$refs.formContainers?.reset();
  }

  resetFormValues(): void {
    this.valueEdit = null;

    if (this.mode === "0") {
      this.code = "";
    }
    this.commodity = this.commodities?.[0] ?? null;
    this.currency = this.currencies?.find((c) => c.mainCurrency === true);
    this.amount = null;
    this.$refs.formValues?.reset();
  }

  onEditContainer(value: IValue): void {
    this.type = this.declaredValueTypes.find((t) => {
      if (value.sc) {
        return t.key === "SEAL";
      }
      return t.key === "BAG";
    })!;
    this.code = value.sc ?? value.bc ?? "";

    this.containerEdit = value;
  }

  onSubmitContainer(): void {
    const declaredContainer =
      this.containerEdit ||
      this.declaredContainers.find((value) => {
        if (this.type?.key === "BAG") {
          return value.bc === this.code;
        } else if (this.type?.key === "SEAL") {
          return value.sc === this.code;
        }
        return false;
      });

    if (declaredContainer) {
      declaredContainer.bc = this.type?.key === "BAG" ? this.code : undefined;
      declaredContainer.sc = this.type?.key === "SEAL" ? this.code : undefined;
    } else {
      this.declaredContainers.push({
        bc: this.type?.key === "BAG" ? this.code : undefined,
        sc: this.type?.key === "SEAL" ? this.code : undefined,
      });
    }

    this.resetFormContainers();
  }

  onEditValue(value: IValue): void {
    if (this.mode === "0") {
      this.type = this.declaredValueTypes.find((t) => {
        if (value.sc) {
          return t.key === "SEAL";
        }
        return t.key === "BAG";
      })!;
      this.code = value.sc ?? value.bc ?? "";
    }
    this.commodity = this.commodities.find((c) => c.code === value.ctc) ?? null;
    this.currency = this.currencies.find((c) => c.isoCode === value.cic) ?? null;
    this.amount = value.am!;

    this.valueEdit = value;
  }

  onSubmitValue(): void {
    let declaredValue = this.valueEdit ?? undefined;

    if (!declaredValue && this.mode === "0") {
      declaredValue = this.declaredValues.find((value) => {
        if (this.type?.key === "BAG") {
          return value.bc === this.code;
        } else if (this.type?.key === "SEAL") {
          return value.sc === this.code;
        }
        return false;
      });
    }

    if (declaredValue) {
      declaredValue.bc = this.mode === "0" && this.type?.key === "BAG" ? this.code : undefined;
      declaredValue.sc = this.mode === "0" && this.type?.key === "SEAL" ? this.code : undefined;
      declaredValue.ctc = this.commodity?.code;
      declaredValue.cic = this.currency?.isoCode;
      declaredValue.am = this.amount!;
    } else {
      this.declaredValues.push({
        bc: this.mode === "0" && this.type?.key === "BAG" ? this.code : undefined,
        sc: this.mode === "0" && this.type?.key === "SEAL" ? this.code : undefined,
        ctc: this.commodity?.code,
        cic: this.currency?.isoCode,
        am: this.amount!,
      });
    }

    this.resetFormValues();
  }

  hideModal(): void {
    this.$bvModal.hide("addEditDeclaredValueModal");
  }

  async onSave(): Promise<void> {
    if (!this.someSeal) {
      this.$swal({
        title: this.$t("label.seal.required.title").toString(),
        text: this.$t("label.seal.required.description").toString(),
        icon: "warning",
        confirmButtonText: this.$t("label.ok").toString(),
      });
    } else {
      const request: ISaveDeclaredValueRequestBody = {
        ct: this.getLoggedUser.country!.code!,
        svc: this.serviceCode,
        mo: this.mode,
        dr: [
          {
            co: this.declaredValue?.co,
            vl: this.declaredValues || [],
            ct: this.declaredContainers || [],
          },
        ],
      };

      const limitExceeded = await this.isLimitExceeded(request.dr);
      if (limitExceeded.length > 0) {
        const items = limitExceeded
          .map(
            (l) =>
              `<li>
                ${this.$options.filters?.currency(l.limitValue, this.getCurrencySymbol(l.currency))}
              </li>`
          )
          .join("");
        this.$swal({
          title: this.$t("message.newOrder.limit.exceeded.title").toString(),
          html: `
            ${this.$t("message.newOrder.limits.exceeded.description")}
            <ul class="swal-limits-list">
              ${items}
            </ul>`,
          icon: "warning",
          confirmButtonText: this.$t("label.ok").toString(),
        });
        return;
      }

      let success: boolean;
      if (this.declaredValue) {
        success = await this.$services.solServiceApi.updateDeclaredValue(request);
      } else {
        success = await this.$services.solServiceApi.saveDeclaredValue(request);
      }

      if (success) {
        this.$emit("success");
        this.hideModal();
      }
    }
  }

  async isLimitExceeded(dr: IDeclaredValue[]): Promise<ILimitExceeded[]> {
    const servicepointLimits = await this.$services.servicepoint.getServicepointLimits(
      this.servicepointSelected!.id!,
      SolCustomerOrderType.PICKUP
    );
    const amountsByCurrency = this.getNewAmountsByCurrency(dr);

    let limitExceeded: ILimitExceeded[] = [];

    amountsByCurrency.forEach((value) => {
      const limit = servicepointLimits?.limits.find((l) => l.isoCode === value.currency)?.amount;

      const originalAmount = (this.declaredValue?.vl || [])
        .filter((v) => (v.cic ?? "") === value.currency)
        .reduce((sum, v) => sum + Number(v.am ?? 0), 0);

      const totalAmount =
        this.getBaseAmountByCurrency(value.currency) - originalAmount + value.import;

      if (limit && totalAmount > limit) {
        limitExceeded.push({ currency: value.currency, import: totalAmount, limitValue: limit });
      }
    });
    return limitExceeded;
  }

  getNewAmountsByCurrency(dr: IDeclaredValue[]): ILimitExceeded[] {
    const newValues = dr.flatMap((d) => d.vl || []);
    return [...newValues].reduce((acc, value) => {
      const isoCode = value.cic ?? "";
      const amount = Number(value.am ?? 0);
      const existing = acc.find((item) => item.currency === isoCode);
      if (existing) {
        existing.import += amount;
      } else {
        acc.push({ currency: isoCode, import: amount });
      }
      return acc;
    }, [] as ILimitExceeded[]);
  }

  getBaseAmountByCurrency(currency: string): number {
    const requestedAmount = this.requestedAmountByCurrency.find(
      (amount) => amount.currencyIsoCode === currency
    )?.amount;

    if (requestedAmount != null) {
      return requestedAmount;
    }

    return (this.allDeclaredValues?.dt?.dr || [])
      .flatMap((declared) => declared.vl || [])
      .filter((value) => (value.cic ?? "") === currency)
      .reduce((sum, value) => sum + Number(value.am ?? 0), 0);
  }

  getCurrencySymbol(isoCode: string): string {
    const currency = this.currencies.find((c) => c.isoCode === isoCode);
    return currency?.symbol || isoCode;
  }

  get someSeal(): boolean {
    const list = this.mode === "0" ? this.declaredValues : this.declaredContainers;

    return list.some((item) => item.sc);
  }
}
